import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation } from "react-router-dom";
import { Show, UserButton, useClerk } from "@clerk/react";
import { LogOut, Home, MessagesSquare, Users, Receipt, HandHeart, Image as ImageIcon, Sparkles, Gift, MessageSquareMore, BarChart3, Settings, Loader2, BookOpen, LifeBuoy, Megaphone } from "lucide-react";
import { useStaffApi } from "@/lib/api";
import { BrandLockup } from "@/components/Brand";
import { TeamInstall } from "./TeamInstall";
import { AlertControls, useAdminAlerts } from "./AlertControls";

const C = "/admin/conversas";
const NAV = [
  { to: "/admin", label: "Início", icon: Home, id: "home", exact: true },
  { to: "/admin/participantes", label: "Participantes", icon: Users, id: "participants" },
  { to: C, f: "", label: "Conversas", icon: MessagesSquare, id: "conversations" },
  { to: C, f: "oracao", label: "Pedidos de oração", icon: HandHeart, id: "oracao" },
  { to: C, f: "fotografia", label: "Fotografias", icon: ImageIcon, id: "fotografia" },
  { to: C, f: "testemunho", label: "Testemunhos", icon: Sparkles, id: "testemunho" },
  { to: "/admin/comprovantes", label: "Comprovantes Pix", icon: Receipt, id: "receipts" },
  { to: C, f: "deus_provera", label: "Solicitações Deus Proverá", icon: Gift, id: "deus_provera" },
  { to: C, f: "outras", label: "Outras mensagens", icon: MessageSquareMore, id: "outras" },
  { to: "/admin/respostas", label: "Biblioteca de respostas", icon: BookOpen, id: "library" },
  { to: "/admin/transmissoes", label: "Transmissões", icon: Megaphone, id: "broadcasts" },
  { to: "/admin/relatorios", label: "Relatórios", icon: BarChart3, id: "reports" },
  { to: "/admin/configuracoes", label: "Configurações", icon: Settings, id: "settings", also: ["/admin/pix", "/admin/categorias", "/admin/equipe"] },
  { to: "/admin/ajuda", label: "Ajuda", icon: LifeBuoy, id: "help" },
];

const isActive = (n, pathname, f) => {
  if (n.exact) return pathname === n.to;
  if (n.f !== undefined) return pathname === n.to && f === n.f;
  return pathname.startsWith(n.to) || (n.also || []).some((p) => pathname.startsWith(p));
};

const Nav = () => {
  const { pathname, search } = useLocation();
  const f = new URLSearchParams(search).get("f") || "";
  return (
    <nav className="scroll-thin flex gap-1 overflow-x-auto md:flex-col md:overflow-y-auto">
      {NAV.map((n) => {
        const active = isActive(n, pathname, f);
        return (
          <Link key={n.id} to={n.f ? `${n.to}?f=${n.f}` : n.to} data-testid={`admin-nav-${n.id}`} aria-current={active ? "page" : undefined}
            className={`flex shrink-0 items-center gap-3 rounded-md px-3 py-2 text-[14px] font-medium transition-colors ${active ? "bg-white/10 text-white shadow-[inset_3px_0_0_#C9A24A]" : "text-white/70 hover:bg-white/5 hover:text-white"}`}>
            <n.icon className="h-[18px] w-[18px] shrink-0" /><span className="whitespace-nowrap">{n.label}</span>
          </Link>
        );
      })}
    </nav>
  );
};

const SignOutButton = ({ className = "" }) => {
  const { signOut } = useClerk();
  return (
    <button data-testid="admin-sign-out" onClick={() => signOut({ redirectUrl: "/admin/entrar" })}
      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors ${className}`}>
      <LogOut className="h-4 w-4" /> Sair
    </button>
  );
};

const Pending = () => (
  <div data-testid="admin-pending" className="flex min-h-[100dvh] items-center justify-center bg-zap-mist p-6">
    <div className="max-w-md space-y-4 text-center">
      <p className="font-display text-2xl font-semibold text-zap-deep">Acesso aguardando aprovação</p>
      <p className="text-[16px] text-muted-foreground">Sua conta foi criada. Um administrador da igreja precisa liberar o seu acesso. Nesta versão, somente o Administrador Geral acessa o painel.</p>
      <div className="flex justify-center"><SignOutButton className="border border-zap-line bg-white hover:bg-zap-line" /></div>
    </div>
  </div>
);

function StaffShell() {
  const api = useStaffApi();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const alerts = useAdminAlerts(api, me?.role === "admin");
  const [error, setError] = useState(false);
  const [categories, setCategories] = useState([]);
  const reloadCategories = useCallback(() => api.get("/admin/categories").then((r) => setCategories(r.data)).catch(() => {}), [api]);
  useEffect(() => {
    api.get("/me").then((r) => setMe(r.data)).catch(() => setError(true));
  }, [api]);
  useEffect(() => {
    if (me && me.role === "admin") reloadCategories();
  }, [me, reloadCategories]);

  if (error) return <p className="p-8 text-[16px]">Não foi possível carregar o painel. Atualize a página.</p>;
  if (!me) return <div className="flex h-[100dvh] items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-zap-deep" /></div>;
  if (me.role !== "admin") return <Pending />;
  const inChat = location.pathname.startsWith("/admin/conversas") && new URLSearchParams(location.search).has("p");
  return (
    <div data-testid="admin-shell" className="flex h-[100dvh] flex-col bg-zap-mist md:flex-row">
      <aside className={`${inChat ? "hidden md:flex" : "flex"} shrink-0 flex-col gap-4 bg-zap-ink px-3 py-3 text-white md:w-60 md:py-5`}>
        <div className="flex items-center justify-between gap-2 px-2 md:block">
          <div className="min-w-0 max-md:[&>div>div:last-child]:hidden min-[480px]:max-md:[&>div>div:last-child]:block">
            <BrandLockup testId="admin-brand" logoClass="h-11" compact />
            <p className="mt-1 hidden text-xs text-white/55 md:block">Painel de atendimento</p>
          </div>
          <div className="flex items-center gap-2 md:hidden"><AlertControls alerts={alerts} where="top" /><TeamInstall /><UserButton /></div>
        </div>
        <Nav />
        <div className="mt-auto hidden space-y-2 md:block"><AlertControls alerts={alerts} where="side" /><TeamInstall /></div>
        <div className="hidden items-center gap-3 border-t border-white/10 px-2 pt-4 md:flex">
          <UserButton />
          <div className="min-w-0 text-sm">
            <p data-testid="admin-user-name" className="truncate font-medium">{me.name}</p>
            <p className="text-white/55">Administrador Geral</p>
          </div>
        </div>
        <SignOutButton className="hidden text-white/70 hover:bg-white/5 hover:text-white md:flex" />
      </aside>
      <section className="min-h-0 min-w-0 flex-1 overflow-hidden">
        <Outlet context={{ api, me, categories, reloadCategories }} />
      </section>
    </div>
  );
}

export default function AdminLayout() {
  return (
    <>
      <Show when="signed-out"><Navigate to="/admin/entrar" replace /></Show>
      <Show when="signed-in"><StaffShell /></Show>
    </>
  );
}
